import type { DashboardNarrativeContent } from "src/components/common";
import type {
  PlatformReportBundle,
  PlatformReportRow,
  PlatformReportTotals,
} from "../../data/paidMediaPlatformReportMockData";

interface SummaryFormatters {
  // eslint-disable-next-line no-unused-vars
  formatMoney: (value: number | null) => string;
  // eslint-disable-next-line no-unused-vars
  formatUnitCost: (value: number | null) => string;
  // eslint-disable-next-line no-unused-vars
  formatCount: (value: number | null) => string;
  // eslint-disable-next-line no-unused-vars
  formatPercent: (value: number | null) => string;
  // eslint-disable-next-line no-unused-vars
  formatRoas: (value: number | null) => string;
  isRTL: boolean;
}

interface BuildSummaryParams {
  platformLabel: string;
  clickLabel: string;
  purchasesLabel: string;
  report: PlatformReportBundle;
  startDate: string;
  endDate: string;
  fmt: SummaryFormatters;
}

/**
 * Wraps a formatted value in `++...++` / `--...--` so `DashboardNarrativePanel`
 * tints it green/red, driven by that metric's own delta. Cost metrics pass
 * `invert` because a drop in CPC/CPA is the good direction.
 */
const colorize = (formattedValue: string, delta: number | null, invert = false): string => {
  if (delta === null || Math.abs(delta) < 2) {
    return formattedValue;
  }
  const good = invert ? delta < 0 : delta > 0;
  return good ? `++${formattedValue}++` : `--${formattedValue}--`;
};

const changePercent = (
  current: number | null | undefined,
  previous: number | null | undefined
): number | null => {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return null;
  }
  if (previous === 0) {
    return current === 0 ? 0 : null;
  }
  return Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
};

function movement(delta: number | null, isRTL: boolean): string {
  if (delta === null) {
    return isRTL ? "تغيّر" : "changed";
  }
  const abs = Math.abs(delta);
  const direction = delta >= 0 ? (isRTL ? "ارتفع" : "rose") : (isRTL ? "انخفض" : "fell");

  if (abs < 2) {
    return isRTL ? "ظل مستقراً نسبياً" : "stayed broadly flat";
  }
  if (abs < 5) {
    return isRTL ? `${direction} قليلاً` : `${direction} slightly`;
  }
  if (abs <= 15) {
    return direction;
  }
  return isRTL ? `${direction} بشكل ملحوظ` : `${direction} sharply`;
}

const bySpend = (rows: PlatformReportRow[]): PlatformReportRow[] =>
  [...rows].sort((a, b) => b.spend - a.spend);

/** Rows ranked best-to-worst on ROAS, ignoring anything that did not spend. */
const rankedByRoas = (rows: PlatformReportRow[]): PlatformReportRow[] =>
  rows
    .filter(row => row.roas !== null && row.spend > 0)
    .sort((a, b) => (b.roas ?? 0) - (a.roas ?? 0));

const efficiency = (totals: PlatformReportTotals) => ({
  roas: totals.roas,
  cpc: totals.cpc,
  costPerPurchase: totals.costPerPurchase,
  ctr: totals.ctr,
});

function buildLeadSummary(params: BuildSummaryParams): string {
  const { report, fmt, platformLabel, clickLabel, purchasesLabel } = params;
  const { isRTL } = fmt;
  const { totals, priorTotals } = report;

  const spendDelta = changePercent(totals.spend, priorTotals.spend);
  const spendText = fmt.formatMoney(totals.spend);
  const revenueDelta = changePercent(totals.revenue, priorTotals.revenue);
  const revenueText = colorize(fmt.formatMoney(totals.revenue), revenueDelta);
  const roasDelta = changePercent(totals.roas, priorTotals.roas);
  const roasText = colorize(fmt.formatRoas(totals.roas), roasDelta);
  const purchasesDelta = changePercent(totals.purchases, priorTotals.purchases);
  const purchasesText = colorize(fmt.formatCount(totals.purchases), purchasesDelta);

  const topCampaign = bySpend(report.campaigns)[0];
  const topCampaignSentence = topCampaign
    ? isRTL
      ? ` تستحوذ حملة "${topCampaign.name}" على أعلى إنفاق بقيمة ${fmt.formatMoney(topCampaign.spend)} وعائد ${fmt.formatRoas(topCampaign.roas)}.`
      : ` "${topCampaign.name}" carries the highest spend at ${fmt.formatMoney(topCampaign.spend)} on ${fmt.formatRoas(topCampaign.roas)} ROAS.`
    : "";

  return isRTL
    ? `بلغ إنفاق ${platformLabel} ${spendText}، وقد ${movement(spendDelta, isRTL)} مقارنة بالفترة السابقة، محققاً ${revenueText} إيرادات بعائد على الإنفاق الإعلاني ${roasText}. سجّلت الحملات ${purchasesText} ${purchasesLabel} من ${fmt.formatCount(totals.clicks)} ${clickLabel} و${fmt.formatCount(totals.impressions)} ظهور.${topCampaignSentence}`
    : `${platformLabel} spend reached ${spendText}, having ${movement(spendDelta, isRTL)} versus the prior period, returning ${revenueText} in revenue at ${roasText} ROAS. Campaigns drove ${purchasesText} ${purchasesLabel.toLowerCase()} from ${fmt.formatCount(totals.clicks)} ${clickLabel.toLowerCase()} and ${fmt.formatCount(totals.impressions)} impressions.${topCampaignSentence}`;
}

function buildInsights(params: BuildSummaryParams): string[] {
  const { report, fmt, clickLabel, purchasesLabel } = params;
  const { isRTL } = fmt;
  const current = efficiency(report.totals);
  const prior = efficiency(report.priorTotals);
  const insights: string[] = [];

  const roasDelta = changePercent(current.roas, prior.roas);
  insights.push(
    isRTL
      ? `العائد على الإنفاق الإعلاني ${colorize(fmt.formatRoas(current.roas), roasDelta)}، وقد ${movement(roasDelta, isRTL)} مقارنة بـ ${fmt.formatRoas(prior.roas)} في الفترة السابقة.`
      : `ROAS is ${colorize(fmt.formatRoas(current.roas), roasDelta)}, having ${movement(roasDelta, isRTL)} from ${fmt.formatRoas(prior.roas)} in the prior period.`
  );

  const cpaDelta = changePercent(current.costPerPurchase, prior.costPerPurchase);
  insights.push(
    isRTL
      ? `تكلفة ${purchasesLabel} ${colorize(fmt.formatUnitCost(current.costPerPurchase), cpaDelta, true)}، وقد ${movement(cpaDelta, isRTL)} مقارنة بالفترة السابقة.`
      : `Cost per ${purchasesLabel.toLowerCase()} is ${colorize(fmt.formatUnitCost(current.costPerPurchase), cpaDelta, true)}, having ${movement(cpaDelta, isRTL)} versus the prior period.`
  );

  const ctrDelta = changePercent(current.ctr, prior.ctr);
  const cpcDelta = changePercent(current.cpc, prior.cpc);
  insights.push(
    isRTL
      ? `معدل النقر إلى الظهور ${colorize(fmt.formatPercent(current.ctr), ctrDelta)} بتكلفة ${clickLabel} ${colorize(fmt.formatUnitCost(current.cpc), cpcDelta, true)}.`
      : `Click-through rate is ${colorize(fmt.formatPercent(current.ctr), ctrDelta)} at ${colorize(fmt.formatUnitCost(current.cpc), cpcDelta, true)} per ${clickLabel.toLowerCase()}.`
  );

  const ranked = rankedByRoas(report.campaigns);
  const best = ranked[0];
  const worst = ranked.length > 1 ? ranked[ranked.length - 1] : undefined;
  if (best) {
    insights.push(
      isRTL
        ? `أفضل حملة من حيث العائد هي "${best.name}" بعائد ${fmt.formatRoas(best.roas)} على إنفاق ${fmt.formatMoney(best.spend)}.`
        : `"${best.name}" is the strongest campaign at ${fmt.formatRoas(best.roas)} ROAS on ${fmt.formatMoney(best.spend)} spend.`
    );
  }
  if (best && worst && worst.id !== best.id) {
    insights.push(
      isRTL
        ? `أضعف حملة هي "${worst.name}" بعائد ${fmt.formatRoas(worst.roas)} على إنفاق ${fmt.formatMoney(worst.spend)}.`
        : `"${worst.name}" is the weakest at ${fmt.formatRoas(worst.roas)} ROAS on ${fmt.formatMoney(worst.spend)} spend.`
    );
  }

  const topCreative = rankedByRoas(report.creatives)[0];
  if (topCreative) {
    insights.push(
      isRTL
        ? `على مستوى الإبداعات، يتصدر "${topCreative.name}" بعائد ${fmt.formatRoas(topCreative.roas)}.`
        : `At the creative level, "${topCreative.name}" leads with ${fmt.formatRoas(topCreative.roas)} ROAS.`
    );
  }

  return insights;
}

function buildAction(params: BuildSummaryParams): string | null {
  const { report, fmt } = params;
  const { isRTL } = fmt;
  const ranked = rankedByRoas(report.campaigns);

  if (ranked.length < 2) {
    return isRTL
      ? "استمر في مراقبة العائد على الإنفاق أسبوعياً قبل تعديل الميزانيات."
      : "Keep monitoring ROAS weekly before making budget moves.";
  }

  const best = ranked[0];
  const worst = ranked[ranked.length - 1];
  if (best.id === worst.id) {
    return null;
  }

  return isRTL
    ? `حوّل جزءاً من ميزانية "${worst.name}" (عائد ${fmt.formatRoas(worst.roas)}) إلى "${best.name}" (عائد ${fmt.formatRoas(best.roas)}).`
    : `Shift budget from "${worst.name}" (${fmt.formatRoas(worst.roas)} ROAS) toward "${best.name}" (${fmt.formatRoas(best.roas)} ROAS).`;
}

function formatPeriodLabel(startIso: string, endIso: string, isRTL: boolean): string {
  const start = new Date(`${startIso}T00:00:00Z`);
  const end = new Date(`${endIso}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "";
  }
  const formatter = new Intl.DateTimeFormat(isRTL ? "ar-EG-u-nu-arab" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
  return `${formatter.format(start)} – ${formatter.format(end)}`;
}

export interface PlatformReportExecutiveSummary extends DashboardNarrativeContent {
  hasData: boolean;
  periodLabel: string;
  action: string | null;
}

export function generatePlatformReportExecutiveSummary(
  params: BuildSummaryParams
): PlatformReportExecutiveSummary {
  const { report, startDate, endDate, fmt } = params;
  const periodLabel = formatPeriodLabel(startDate, endDate, fmt.isRTL);
  const hasData = report.totals.spend > 0 || report.totals.impressions > 0;

  if (!hasData) {
    return {
      summary: "",
      keyInsights: [],
      recommendations: [],
      hasData: false,
      periodLabel,
      action: null,
    };
  }

  const action = buildAction(params);
  // The mock builder can return a thin period for very short ranges; flag it so
  // the narrative does not read as a confident read on three days of data.
  const lowData = report.campaigns.length < 3 || report.series.length < 7;
  const lowDataPrefix = lowData
    ? fmt.isRTL
      ? "بيانات محدودة لهذه الفترة — "
      : "Limited data for this period — "
    : "";

  return {
    summary: `${lowDataPrefix}${buildLeadSummary(params)}`,
    keyInsights: buildInsights(params),
    recommendations: action ? [action] : [],
    hasData: true,
    periodLabel,
    action,
  };
}
